/**
 * Checkout Success Page Component.
 * Handles successful payment completion for both authenticated and anonymous users.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Mail, ArrowRight, Star, User, Lock } from 'lucide-react';
import { useMetaPixel } from '@/hooks/useMetaPixel';
import { SUBSCRIPTION_PLANS } from '@/config/plan-config';

export const CheckoutSuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { trackPurchase } = useMetaPixel();

  useEffect(() => {
    const emailParam = searchParams.get('email');
    setEmail(emailParam);
    setIsLoading(false);

    // Track purchase event with Meta Pixel
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      const allParams = Object.fromEntries(searchParams.entries());

      const interval = searchParams.get('interval') ?? 'monthly';
      const purchaseValue = SUBSCRIPTION_PLANS.find((p) => p.id === interval)?.price || 0;
      const currency = 'USD';
      const plan = 'premium';
      trackPurchase(purchaseValue, currency, {
        ...allParams,
        content_category: 'OpenSelf',
        content_ids: [sessionId],
        content_name: 'Premium Subscription',
        content_type: 'subscription',
        currency: currency,
        email: emailParam,
        plan: plan,
        session_id: sessionId,
        value: purchaseValue,
      });
    }
  }, []);

  const handleCreateAccount = () => {
    // Navigate to signup with email pre-filled
    const signupUrl = email ? `/signup?email=${encodeURIComponent(email)}` : '/signup';
    navigate(signupUrl);
  };

  const handleSignIn = () => {
    // Navigate to login with email pre-filled
    const loginUrl = email ? `/login?email=${encodeURIComponent(email)}` : '/login';
    navigate(loginUrl);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-kind-deep flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500 mx-auto mb-4"></div>
          <p className="text-kind-text-secondary">Loading your subscription details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-kind-deep">
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Success Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-600 rounded-full mb-6">
            <CheckCircle className="h-10 w-10 text-white" />
          </div>

          <h1 className="text-4xl font-bold text-kind-text-primary mb-4">Payment Successful!</h1>
          <p className="text-xl text-kind-text-secondary mb-6">
            Your subscription is now active. Welcome to premium!
          </p>

          {email && (
            <div className="inline-flex items-center space-x-2 bg-kind-container rounded-lg px-4 py-2 border border-gray-300 dark:border-gray-700">
              <Mail className="h-4 w-4 text-kind-text-secondary" />
              <span className="text-kind-text-primary">{email}</span>
            </div>
          )}
        </div>

        {/* Next Steps */}
        <div className="bg-kind-container rounded-2xl shadow-lg border border-gray-300 dark:border-gray-700 p-8 mb-8">
          <h2 className="text-2xl font-bold text-kind-text-primary mb-6">Next Steps</h2>

          <div className="space-y-6">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-violet-600 rounded-full flex items-center justify-center">
                <span className="text-white font-semibold text-sm">1</span>
              </div>
              <div>
                <h3 className="font-semibold text-kind-text-primary mb-2">Create Your Account</h3>
                <p className="text-kind-text-secondary text-sm mb-4">
                  Set up your account to access all premium features and manage your subscription.
                </p>
                <button
                  onClick={handleCreateAccount}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600 text-white font-medium rounded-lg hover:bg-violet-700 transition-colors"
                >
                  <User className="h-4 w-4" />
                  Create Account
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-violet-600 rounded-full flex items-center justify-center">
                <span className="text-white font-semibold text-sm">2</span>
              </div>
              <div>
                <h3 className="font-semibold text-kind-text-primary mb-2">
                  Already have an account?
                </h3>
                <p className="text-kind-text-secondary text-sm mb-4">
                  Sign in with your existing account to link your subscription.
                </p>
                <button
                  onClick={handleSignIn}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-700 text-kind-text-primary font-medium rounded-lg hover:bg-kind-graphite transition-colors"
                >
                  <Lock className="h-4 w-4" />
                  Sign In
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* What You Get */}
        <div className="bg-kind-container rounded-2xl shadow-lg border border-gray-300 dark:border-gray-700 p-8">
          <h2 className="text-2xl font-bold text-kind-text-primary mb-6">What You Get</h2>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <Star className="h-5 w-5 text-violet-500 flex-shrink-0" />
                <span className="text-kind-text-primary">Unlimited AI Companions</span>
              </div>
              <div className="flex items-center space-x-3">
                <Star className="h-5 w-5 text-violet-500 flex-shrink-0" />
                <span className="text-kind-text-primary">Unlimited Conversations</span>
              </div>
              <div className="flex items-center space-x-3">
                <Star className="h-5 w-5 text-violet-500 flex-shrink-0" />
                <span className="text-kind-text-primary">Priority Support</span>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <Star className="h-5 w-5 text-violet-500 flex-shrink-0" />
                <span className="text-kind-text-primary">Advanced Features</span>
              </div>
              <div className="flex items-center space-x-3">
                <Star className="h-5 w-5 text-violet-500 flex-shrink-0" />
                <span className="text-kind-text-primary">Early Access</span>
              </div>
              <div className="flex items-center space-x-3">
                <Star className="h-5 w-5 text-violet-500 flex-shrink-0" />
                <span className="text-kind-text-primary">Cancel Anytime</span>
              </div>
            </div>
          </div>
        </div>

        {/* Support */}
        <div className="text-center mt-8">
          <p className="text-kind-text-secondary text-sm mb-4">
            Need help? Contact our support team
          </p>
          <div className="flex items-center justify-center space-x-6 text-sm">
            <a
              href="mailto:support@example.com"
              className="text-violet-400 hover:text-violet-300 transition-colors"
            >
              support@openself.ai
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccessPage;
